"use client";

import Link from "next/link";
import { ArrowRight, Layers, Network, ChevronRight } from "lucide-react";
import { Container, Section } from "@/components/ui/container";
import { SectionHeading } from "@/components/ui/section-heading";
import { Reveal } from "@/components/ui/reveal";
import { Counter } from "@/components/ui/counter";
import {
  HubHero, HubOverview, HubFeatures, HubUseCases, HubWhy, HubFAQ, HubCTA,
  CategoryHubComposer,
} from "@/components/ui/category-hub";
import {
  servicesStats, servicesTopLevel, servicesSubcategories,
  servicesFeatures, servicesUseCases, servicesWhy, servicesFaq, servicesCta,
} from "@/lib/data/services-hub";

/* ============================================================
   BESPOKE 1 — Service taxonomy visualization.
   Shows the 10 top-level services grouped into 9 subcategory hubs,
   with a radial layout that visualizes the relationship.
   ============================================================ */
function ServiceTaxonomyMap() {
  const subs = servicesSubcategories;
  const ROUND = (n: number) => Math.round(n * 100) / 100;
  const cx = 260;
  const cy = 260;
  const radius = 180;
  return (
    <div className="relative w-full max-w-[560px] aspect-square mx-auto">
      <svg viewBox="0 0 520 520" className="w-full h-full" aria-hidden>
        <defs>
          <radialGradient id="scvCore" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx={cx} cy={cy} r="210" fill="url(#scvCore)" />
        <circle cx={cx} cy={cy} r={radius} fill="none" stroke="rgb(255 255 255 / 0.07)" strokeDasharray="2 5" />
        <circle cx={cx} cy={cy} r={radius - 60} fill="none" stroke="rgb(255 255 255 / 0.05)" strokeDasharray="1 3" />

        {/* Lines from each sub to center */}
        {subs.map((s, i) => {
          const angle = (i / subs.length) * Math.PI * 2 - Math.PI / 2;
          const x = ROUND(cx + Math.cos(angle) * radius);
          const y = ROUND(cy + Math.sin(angle) * radius);
          return (
            <line
              key={`l-${s.code}`}
              x1={cx}
              y1={cy}
              x2={x}
              y2={y}
              stroke={`var(--color-${s.accent})`}
              strokeOpacity="0.3"
              strokeWidth="0.6"
              strokeDasharray="2 4"
            />
          );
        })}

        {/* Subcategory nodes */}
        {subs.map((s, i) => {
          const angle = (i / subs.length) * Math.PI * 2 - Math.PI / 2;
          const x = ROUND(cx + Math.cos(angle) * radius);
          const y = ROUND(cy + Math.sin(angle) * radius);
          return (
            <g key={s.code} transform={`translate(${x}, ${y})`}>
              <circle r="32" fill={`color-mix(in oklch, var(--color-${s.accent}) 18%, transparent)`} stroke={`var(--color-${s.accent})`} strokeWidth="0.8" />
              <text textAnchor="middle" y="-2" fontSize="7" fontWeight="800" fill="white" letterSpacing="0.4">
                {s.code}
              </text>
              <text textAnchor="middle" y="8" fontSize="5" fill="rgb(255 255 255 / 0.7)" letterSpacing="0.3">
                {s.children.length} SERVICES
              </text>
            </g>
          );
        })}

        {/* Center hub */}
        <g transform={`translate(${cx}, ${cy})`}>
          <circle r="58" fill="rgb(10 10 18 / 0.92)" />
          <circle r="50" fill="none" stroke="rgb(139 92 246 / 0.5)" strokeWidth="1" />
          <text x="0" y="-4" textAnchor="middle" fontSize="22" fontWeight="900" fill="white" letterSpacing="-0.5">
            X2
          </text>
          <text x="0" y="12" textAnchor="middle" fontSize="6" fontWeight="700" fill="#8b5cf6" letterSpacing="2">
            SERVICES
          </text>
          <text x="0" y="22" textAnchor="middle" fontSize="5" fill="rgb(255 255 255 / 0.5)" letterSpacing="1.5">
            10 · 9 · 40+
          </text>
        </g>
      </svg>
      <div className="absolute top-2 right-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "0s" }}>
        <Layers className="h-3.5 w-3.5" style={{ color: "var(--color-purple)" }} />
        <span className="text-xs font-semibold">9 Subcategory Hubs</span>
      </div>
      <div className="absolute bottom-2 left-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "1.5s" }}>
        <Network className="h-3.5 w-3.5" style={{ color: "var(--color-cyan)" }} />
        <span className="text-xs font-semibold">40+ Specialized Services</span>
      </div>
    </div>
  );
}

/* ============================================================
   BESPOKE 2 — Subcategory hubs grid (with child links)
   ============================================================ */
function SubcategoryHubsGrid() {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {servicesSubcategories.map((s, i) => (
        <Reveal key={s.code} delay={i * 70}>
          <div
            className="glass rounded-2xl p-6 h-full card-glow flex flex-col"
            style={{ ["--glow" as string]: `var(--color-${s.accent})` }}
          >
            <div className="flex items-start justify-between mb-3">
              <span
                className="grid place-items-center h-12 w-12 rounded-xl"
                style={{
                  background: `color-mix(in oklch, var(--color-${s.accent}) 22%, transparent)`,
                  boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${s.accent}) 40%, transparent)`,
                }}
              >
                <span className="text-sm font-bold tracking-wider" style={{ color: `var(--color-${s.accent})` }}>
                  {s.code}
                </span>
              </span>
              <Link
                href={s.href}
                className="text-xs font-bold flex items-center gap-1 hover:gap-2 transition-all"
                style={{ color: `var(--color-${s.accent})` }}
              >
                Open Hub <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
            <h3 className="text-lg font-bold">{s.title}</h3>
            <p className="mt-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed flex-1">
              {s.description}
            </p>
            <div className="mt-4 pt-4 border-t border-[rgb(var(--border)/0.12)]">
              <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] font-semibold uppercase tracking-wider mb-2">
                Child Services
              </div>
              <ul className="space-y-1.5">
                {s.children.map((c) => (
                  <li key={c.href}>
                    <Link
                      href={c.href}
                      className="text-xs flex items-center gap-1.5 hover:gap-2 transition-all text-[rgb(var(--fg-muted))] hover:text-[rgb(var(--fg))]"
                    >
                      <ChevronRight className="h-3 w-3 shrink-0" />
                      <span className="truncate">{c.title}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  );
}

/* ============================================================
   BESPOKE 3 — Service response speed comparison (animated bars)
   ============================================================ */
function ResponseSpeedChart() {
  const data = [
    { label: "Traditional PR", hours: 168, accent: "var(--color-amber)", display: "1 week" },
    { label: "Reputation Agency", hours: 72, accent: "var(--color-denim)", display: "3 days" },
    { label: "In-house Team", hours: 48, accent: "var(--color-cyan)", display: "2 days" },
    { label: "Monitoring Tools", hours: 12, accent: "var(--color-violet)", display: "12 hours" },
    { label: "Perception X2", hours: 0.78, accent: "var(--color-crimson)", display: "< 1 hour" },
  ];
  const max = 168;
  return (
    <div className="space-y-3">
      {data.map((d, i) => {
        const pct = d.hours === 0.78 ? 1.2 : (d.hours / max) * 100;
        const isUs = d.label === "Perception X2";
        return (
          <Reveal key={d.label} delay={i * 80}>
            <div className={`grid grid-cols-[180px_1fr_120px] items-center gap-3 ${isUs ? "py-1" : ""}`}>
              <div className="text-xs font-semibold flex items-center gap-2">
                {isUs && (
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-emerald)] animate-pulse" />
                )}
                {d.label}
              </div>
              <div className="relative h-9 rounded-lg bg-[rgb(var(--border)/0.08)] overflow-hidden flex items-center px-3">
                <div
                  className="absolute left-0 top-0 h-full rounded-lg transition-all"
                  style={{
                    width: `${pct}%`,
                    background: `linear-gradient(90deg, ${d.accent}, color-mix(in oklch, ${d.accent} 50%, transparent))`,
                    opacity: isUs ? 1 : 0.6,
                  }}
                />
                <span className="relative text-[10px] font-mono-num font-bold text-white z-10">
                  {d.display}
                </span>
              </div>
              <div className="text-right text-sm font-bold font-mono-num" style={{ color: d.accent }}>
                {isUs ? (
                  <Counter to={47} suffix=" min" />
                ) : (
                  `${d.hours}h`
                )}
              </div>
            </div>
          </Reveal>
        );
      })}
    </div>
  );
}

/* ============================================================
   PAGE COMPOSER
   ============================================================ */
export function ServicesHubPage() {
  return (
    <CategoryHubComposer
      overviewWrap="crimson"
      featuresWrap="purple"
      useCasesWrap="emerald"
      whyWrap="cyan"
      faqWrap="amber"
      ctaWrap="violet"
      hero={
        <section id="top" className="relative overflow-hidden pt-8 pb-16 sm:pt-12 sm:pb-24">
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute top-10 left-1/4 h-72 w-72 rounded-full bg-[var(--color-purple)] opacity-22 blur-[130px] animate-pulse-glow" />
            <div className="absolute top-32 right-1/4 h-80 w-80 rounded-full bg-[var(--color-crimson)] opacity-18 blur-[140px] animate-pulse-glow" style={{ animationDelay: "2s" }} />
            <div className="absolute bottom-10 left-1/3 h-72 w-72 rounded-full bg-[var(--color-cyan)] opacity-15 blur-[140px] animate-pulse-glow" style={{ animationDelay: "4s" }} />
          </div>
          <Container size="wide">
            <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-6 items-center">
              <div className="flex flex-col items-start gap-6">
                <Reveal>
                  <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full glass">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--color-emerald)] opacity-75 animate-ping" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--color-emerald)]" />
                    </span>
                    <span className="eyebrow-tiny uppercase tracking-[0.28em]">Perception X2 Services Catalog</span>
                  </div>
                </Reveal>
                <Reveal delay={80}>
                  <h1 className="display-hero text-balance">
                    Service Verticals{" "}
                    <span className="bg-gradient-to-br from-[var(--color-purple)] via-[var(--color-crimson)] to-[var(--color-amber)] bg-clip-text text-transparent">
                      on One Autonomous Platform
                    </span>
                  </h1>
                </Reveal>
                <Reveal delay={160}>
                  <p className="text-[1.35rem] sm:text-[1.5rem] leading-[1.45] font-medium text-[rgb(var(--fg))] max-w-2xl">
                    10 service verticals. 9 subcategory hubs. 40+ specialized services. Every capability built on the same Perception X2 platform — the world's most advanced perception amplification stack.
                  </p>
                </Reveal>
                <Reveal delay={220}>
                  <p className="text-body leading-relaxed text-[rgb(var(--fg-muted))] max-w-2xl">
                    From brand protection to crisis response, narrative engineering to competitive intelligence — each service inherits the platform's 384×–1,416× faster response, 95–98% organic authenticity, and 15+ years of operational refinement.
                  </p>
                </Reveal>
                <Reveal delay={280}>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <Link
                      href="/services/brand-protection-augmentation"
                      className="btn-primary-gradient h-14 px-8 inline-flex items-center gap-2 rounded-full font-semibold text-base shadow-2xl shadow-[var(--color-purple)/30]"
                    >
                      Explore Brand Protection <ArrowRight className="h-4 w-4" />
                    </Link>
                    <Link
                      href="/services/crisis-management-response"
                      className="btn-glass h-14 px-8 inline-flex items-center gap-2 rounded-full font-semibold text-base"
                    >
                      Crisis Response
                    </Link>
                  </div>
                </Reveal>
                <Reveal delay={360}>
                  <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 max-w-3xl w-full">
                    {servicesStats.map((s) => (
                      <div
                        key={s.label}
                        className="glass rounded-2xl p-3 card-glow"
                        style={{ ["--glow" as string]: `var(--color-${s.accent})` }}
                      >
                        <div className="display-stat-sm" style={{ color: `var(--color-${s.accent})` }}>
                          <Counter to={s.to} suffix={s.suffix ?? ""} decimals={s.decimals ?? 0} />
                        </div>
                        <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] mt-1.5 font-semibold uppercase tracking-wider">
                          {s.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </Reveal>
              </div>
              <Reveal delay={200} className="order-first lg:order-last">
                <ServiceTaxonomyMap />
              </Reveal>
            </div>
          </Container>
        </section>
      }
      overview={
        <>
          <Section id="top-level" className="relative">
            <Container>
              <SectionHeading
                eyebrow="Top-Level Services"
                title="The Ten Service Verticals"
                description="Perception X2 organizes its service catalog into ten verticals — each a coherent operational capability with its own sub-disciplines."
                accent="crimson"
              />
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {servicesTopLevel.map((s, i) => (
                  <Reveal key={s.href} delay={i * 60}>
                    <Link
                      href={s.href}
                      className="group block glass rounded-2xl p-6 h-full hover:scale-[1.02] transition-transform"
                      style={{
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${s.accent}) 12%, transparent)`,
                      }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span
                          className="grid place-items-center h-10 w-10 rounded-lg"
                          style={{
                            background: `color-mix(in oklch, var(--color-${s.accent}) 18%, transparent)`,
                            boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${s.accent}) 35%, transparent)`,
                          }}
                        >
                          <span className="text-[10px] font-bold tracking-wider" style={{ color: `var(--color-${s.accent})` }}>
                            {s.code}
                          </span>
                        </span>
                        <ChevronRight className="h-4 w-4 text-[rgb(var(--fg-subtle))] group-hover:translate-x-1 transition-transform" />
                      </div>
                      <h3 className="text-base font-bold">{s.title}</h3>
                      <p className="mt-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{s.description}</p>
                      <div className="mt-4 text-xs font-semibold" style={{ color: `var(--color-${s.accent})` }}>
                        Explore Service →
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </Container>
          </Section>
          <Section id="subcategories" className="relative mt-8">
            <Container>
              <SectionHeading
                eyebrow="Subcategory Hubs"
                title="Nine Specialized Domain Hubs"
                description="Each subcategory hub aggregates related services into a single operational view — with deep links to every specialized engagement."
                accent="purple"
              />
              <SubcategoryHubsGrid />
            </Container>
          </Section>
        </>
      }
      features={
        <>
          <Section id="speed" className="relative">
            <Container>
              <SectionHeading
                eyebrow="Response Speed"
                title="From Days to Under One Hour"
                description="Perception X2 services respond at machine speed. Compare against the operational cadence of traditional agencies, in-house teams, and monitoring-only tools."
                accent="amber"
              />
              <div className="glass rounded-2xl p-6">
                <ResponseSpeedChart />
              </div>
              <div className="mt-6 grid sm:grid-cols-3 gap-4">
                <div className="glass rounded-2xl p-5 text-center">
                  <div className="display-stat-sm text-[var(--color-crimson)]">
                    <Counter to={384} suffix="×" />
                  </div>
                  <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] mt-2 font-semibold uppercase tracking-wider">
                    Faster than In-House
                  </div>
                </div>
                <div className="glass rounded-2xl p-5 text-center">
                  <div className="display-stat-sm text-[var(--color-emerald)]">
                    <Counter to={1416} suffix="×" />
                  </div>
                  <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] mt-2 font-semibold uppercase tracking-wider">
                    Faster than Traditional PR
                  </div>
                </div>
                <div className="glass rounded-2xl p-5 text-center">
                  <div className="display-stat-sm text-[var(--color-cyan)]">
                    <Counter to={95} suffix="%" />
                  </div>
                  <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] mt-2 font-semibold uppercase tracking-wider">
                    Crisis Success Rate
                  </div>
                </div>
              </div>
            </Container>
          </Section>
          <Section id="features" className="relative mt-8">
            <Container>
              <HubFeatures
                eyebrow="Service Capabilities"
                title="What Every Service Inherits"
                description="Eight foundational capabilities shared across every Perception X2 service vertical."
                features={servicesFeatures}
                accent="purple"
              />
            </Container>
          </Section>
        </>
      }
      useCases={
        <HubUseCases
          eyebrow="Service Applications"
          title="Six Client Archetypes"
          description="Perception X2 services serve six primary client archetypes — each with bespoke operational tuning."
          cases={servicesUseCases}
          accent="emerald"
        />
      }
      why={
        <HubWhy
          eyebrow="Why These Services"
          title="The Differentiators That Compound"
          items={servicesWhy}
          accent="cyan"
        />
      }
      faq={
        <HubFAQ
          eyebrow="Services FAQ"
          title="Frequently Asked Questions"
          items={servicesFaq}
          accent="amber"
        />
      }
      cta={
        <HubCTA
          eyebrow={servicesCta.eyebrow}
          title={servicesCta.title}
          description={servicesCta.description}
          primary={servicesCta.primary}
          secondary={servicesCta.secondary}
          trustItems={servicesCta.trustItems}
          accent="violet"
        />
      }
    />
  );
}
